import {Button, Grid, Typography, useTheme} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect, useState} from "react";
import {MatrixInitializationValues} from "../../../api/types/ancillaryDetail";
import {FormAutocomplete, FormRadioGroupWithOnChange, FormTextField} from "../../../common/form-fields";
import {useTranslation} from "react-i18next";
import {useAncillaryRepository} from "../../../api/repositories/AncillaryRepository";
import {useRecoilState} from "recoil";
import {ancillarySelectAtom} from "../../../state/ancillariesRecoil";
import {SelectElementModelFE} from "../../../api/types/generic";
import {RentalPeriodEntityResponse} from "../../../api/types/rentalPeriods";
import CarGroupsAndRentalPeriods from "../../../common/components/CarGroupsAndRentalPeriods";
import {useFormContext} from "react-hook-form";
import {AncillaryEntityResponse, AncillarySelectFE} from "../../../api/types/ancillary";
import {fromAncillaryBeToFe, i18nConvertNumberWithDecimals, setFormNumberValue} from "../../../utils";
import {useQuery} from "@tanstack/react-query";
import {numberIntegerWithNegativeValuesRegex, numberRegex, numberWithNegativeValuesRegex} from "../../../constants";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const MatrixInitialization = ({
                                  rentalPeriods,
                                  chargeType,
                                  carGroups,
                                  sellList,
                                  handleCancel,
                                  handleSubmit
                              }: {
    rentalPeriods: RentalPeriodEntityResponse[]
    chargeType: string | undefined
    carGroups: string[]
    sellList: SelectElementModelFE[]
    handleCancel: MouseEventHandler<HTMLButtonElement>
    handleSubmit: MouseEventHandler<HTMLButtonElement>
}) => {
    // Use Form Context
    const {setValue, watch, register, getValues} = useFormContext<MatrixInitializationValues>();
    const watchAllForm = watch();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use Recoil
    const [ancillarySelectList, setAncillarySelectList] = useRecoilState(ancillarySelectAtom);

    // Use State
    const [rentalPeriodsSorted, setRentalPeriodSorted] = useState<RentalPeriodEntityResponse[]>();

    // Use Repository Hooks
    const {getCloneableAncillaries} = useAncillaryRepository();
    const {getRentalPeriodsSorted} = useRentalPeriodRepository();
    let showOnlyActive = true;

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    // Use Query
    const queryClonableAncillaries = useQuery<AncillaryEntityResponse[], Error>({
        queryKey: ["PRICING_CLONABLE_ANCILLARIES", chargeType],
        queryFn: () =>
            getCloneableAncillaries(
                chargeType,
                showOnlyActive,
                user.dealers
                    .map((el: any) => el.name)
                    .filter((el: any) => el !== "all"),
                user.legalEntities
                    .map((el: any) => el.name)
                    .filter((el: any) => el !== "all")
            ),
        enabled: !!chargeType,
    });

    useEffect(() => {
        const getStaticAncillaries = async () => {
            if (watchAllForm.matrixInitializationType === 1 && queryClonableAncillaries?.data) {
                const ancillarySelectList: AncillarySelectFE[] = fromAncillaryBeToFe(queryClonableAncillaries.data);

                setAncillarySelectList(ancillarySelectList);
            }
        };

        getStaticAncillaries();

        const getRentalPeriodSorted = async () => {
            const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(rentalPeriods)
            setRentalPeriodSorted(rentalPeriodSorted)
        }
        getRentalPeriodSorted()
        // eslint-disable-next-line
    }, [watchAllForm.matrixInitializationType]);

    return (
        <Grid container spacing={2} p={2} item xs={12}>
            <Grid container spacing={2} p={2} item xs={12}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {t("pricing:ancillaryMatrixInsert.title")}:
                </Typography>
            </Grid>
            <Grid container spacing={2} p={2} item xs={12}>
                <Grid>
                    {/* TIPO DI INIZIALIZZAZIONE */}
                    <FormRadioGroupWithOnChange
                        {...register("matrixInitializationType")}
                        label=""
                        radioButtons={[
                            {value: 0, label: t("pricing:ancillaryMatrixInsert.form.labels.emptyMatrix")},
                            {value: 1, label: t("pricing:ancillaryMatrixInsert.form.labels.copyMatrix")}
                        ]}
                        handleChange={(newValue: string) => setValue("matrixInitializationType", Number(newValue))}
                    />
                </Grid>
                {watchAllForm.matrixInitializationType === 0 &&
                    <Grid container spacing={2} pb={2} item xs={12}>
                        <CarGroupsAndRentalPeriods carGroups={carGroups}
                                                   rentalPeriods={rentalPeriodsSorted as RentalPeriodEntityResponse[]}/>
                        <Grid item xs={12} md={3}>
                            <FormAutocomplete
                                {...register("sell")}
                                label={`${t("pricing:ancillaryMatrixInsert.form.labels.sell")}*`}
                                options={sellList}
                                handleChange={(value: SelectElementModelFE) => setValue("sell", value)}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormTextField
                                {...register("price")}
                                label={`${t("pricing:ancillaryMatrixInsert.form.labels.price")}`}
                                type={"text"}
                                handleChange={(value: string) => setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "price", numberRegex)}
                            />
                        </Grid>
                    </Grid>
                }
                {watchAllForm.matrixInitializationType === 1 &&
                    <Grid container spacing={2} p={2} item xs={12} md={12}>
                        <Grid item xs={12} md={3}>
                            {/* ANCILLARY */}
                            <FormAutocomplete
                                {...register("ancillary")}
                                label={`${t("pricing:ancillary.form.labels.ancillary")}`}
                                options={ancillarySelectList}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                            />
                        </Grid>
                        {/* DISCOUNT INCREASE PERCENTAGE */}
                        <Grid item xs={12} md={3}>
                            <FormTextField
                                {...register("discountIncreasePercentage")}
                                label={`${t("pricing:ancillary.form.labels.discountIncreasePercentage")}`}
                                readOnly={!Boolean(getValues().ancillary)}
                                type={"text"}
                                handleChange={(value: string) => {
                                    setFormNumberValue(value, setValue, "discountIncreasePercentage", numberIntegerWithNegativeValuesRegex);
                                    //if (numberIntegerWithNegativeValuesRegex.test(value.trim())) setValue("discountIncreaseFixed", null);
                                }}
                            />
                        </Grid>
                        {/* DISCOUNT INCREASE FIXED */}
                        <Grid item xs={12} md={3}>
                            <FormTextField
                                {...register("discountIncreaseFixed")}
                                label={`${t("pricing:ancillary.form.labels.discountIncreaseFixed")}`}
                                readOnly={!Boolean(getValues().ancillary)}
                                type={"text"}
                                handleChange={(value: string) => {
                                    setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "discountIncreaseFixed", numberWithNegativeValuesRegex);
                                    //if (numberWithNegativeValuesRegex.test(value.trim())) setValue("discountIncreasePercentage", null);
                                }}
                            />
                        </Grid>
                    </Grid>
                }
            </Grid>
            <Grid container item xs={12} justifyContent="end" columnGap={2}>
                <Button color={"accent"}
                        type={"button"}
                        variant={"outlined"}
                        onClick={handleCancel}
                >
                    {t("pricing:actions.back")}
                </Button>
                <Button color={"accent"}
                        type={"button"}
                        onClick={handleSubmit}
                >
                    {t("pricing:actions.confirm")}
                </Button>
            </Grid>
        </Grid>
    );
};

export default MatrixInitialization;
